import { useCallback, useEffect, useRef, useState } from "react";
import { useInView } from "framer-motion";
import { useVideoVisibility } from "./useVideoVisibility";

// Ambient loops autoplay without controls, including under reduced motion per
// the site owner's explicit choice. Offscreen/hidden-tab pauses only save work.
export const useSectionVideo = (sourceCount) => {
  const frameRef = useRef(null);
  const videoRef = useRef(null);
  const active = useRef(false);
  const blocked = useRef(false);
  const request = useRef(0);
  const failedSource = useRef(null);
  const [status, setStatus] = useState("paused");
  const [hasFrame, setHasFrame] = useState(false);
  const [sourceIndex, setSourceIndex] = useState(0);
  const near = useInView(frameRef, { once: true, margin: "350px 0px" });
  const visible = useInView(frameRef);
  const { pageVisible } = useVideoVisibility();
  const shouldLoad = near;
  const shouldPlay = visible && pageVisible;
  active.current = shouldPlay;

  const pause = useCallback(() => {
    request.current += 1;
    videoRef.current?.pause();
    setStatus((previous) => previous === "error" ? previous : "paused");
  }, []);

  const fail = useCallback(() => {
    // The media error event and play() rejection can describe the same failure.
    if (failedSource.current === sourceIndex) return;
    failedSource.current = sourceIndex;
    request.current += 1;
    setHasFrame(false);
    if (sourceIndex + 1 < sourceCount) {
      setSourceIndex(sourceIndex + 1);
      setStatus("paused");
    } else {
      blocked.current = true;
      setStatus("error");
    }
  }, [sourceIndex, sourceCount]);

  const play = useCallback(() => {
    const video = videoRef.current;
    if (!video || blocked.current || !active.current) return;
    if (!video.paused && video.readyState >= 3) {
      setHasFrame(true);
      setStatus("playing");
      return;
    }
    const attempt = ++request.current;
    video.muted = true;
    setStatus("loading");
    const rejected = (error) => {
      if (attempt !== request.current) return;
      if (error.name === "NotAllowedError") {
        // The browser has final say. Retry within a normal page interaction,
        // without adding a video button or misreporting this as a codec error.
        setStatus("paused");
      } else if (error.name === "AbortError") {
        setStatus("paused");
      } else {
        fail();
      }
    };
    try {
      video.play()?.catch(rejected);
    } catch (error) {
      rejected(error);
    }
  }, [fail]);

  const resume = useCallback(() => {
    if (!active.current || !videoRef.current) return;
    if (blocked.current) {
      blocked.current = false;
      failedSource.current = null;
      setHasFrame(false);
      videoRef.current?.load();
    }
    play();
  }, [play]);

  useEffect(() => {
    if (!shouldLoad) return;
    if (shouldPlay) resume();
    else pause();
  }, [shouldLoad, shouldPlay, resume, pause]);

  useEffect(() => {
    document.addEventListener("pointerdown", resume, { passive: true });
    document.addEventListener("keydown", resume);
    return () => {
      document.removeEventListener("pointerdown", resume);
      document.removeEventListener("keydown", resume);
      request.current += 1;
    };
  }, [resume]);

  const events = {
    onPlaying: () => {
      if (!active.current) {
        pause();
        return;
      }
      setHasFrame(true);
      setStatus("playing");
    },
    onPause: () => setStatus((previous) => previous === "error" ? previous : "paused"),
    onWaiting: () => { if (!videoRef.current?.paused) setStatus("loading"); },
    onLoadedData: play,
    onCanPlay: play,
    onError: (event) => { if (event.currentTarget.error) fail(); },
  };

  return { frameRef, videoRef, shouldLoad, shouldPlay, sourceIndex, status, hasFrame, events };
};